import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { ITreeOptions, TREE_ACTIONS, TreeComponent, TreeModel, TreeNode } from '@ali-hm/angular-tree-component';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ExecuteContextMenuItemEvent } from '../../interfaces/execute-context-menu-item-event';
import { MatDialog } from '@angular/material/dialog';
import { ManipulateFolderComponent } from './components/manipulate-folder/manipulate-folder.component';
import { ManipulateFolderData } from './interfaces/manipulate-folder-data.interface';
import { filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CollectionItem } from './interfaces/collection-item.interface';
import { CollectionItemType } from './enums/collection-item-type.enum';
import { VirtualRootFolderConfig } from './interfaces/virtual-root-folder-config.interface';
import { VIRTUAL_FOLDER_ID } from './folders-constants';
import { DeleteFolderComponent } from './components/delete-folder/delete-folder.component';
import { CreateFolderData } from './interfaces/create-folder-data.interface';
import { FoldersUtilsService } from './services/folders-utils.service';
import { ProcessedNode } from './interfaces/processed-node.interface';
import { CollectionItemMetadata } from './interfaces/collection-item-metadata.interface';
import { Subject } from 'rxjs';
import { MovedItems } from './interfaces/move-items.interface';
import { FoldersService } from './services/folders.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'mee-folders',
  templateUrl: './folders.component.html',
  styleUrls: ['./folders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FoldersComponent implements AfterViewInit, OnDestroy {
  @Input() preselectedItemMetadata: CollectionItemMetadata[] = [];
  @Input() maxNestingLevel = 3;
  @Input({ transform: booleanAttribute }) hideProjectFolders = false;
  @Output() selectedFolderChange = new EventEmitter<string>();
  @Output() folderCreated = new EventEmitter<string>();
  @Output() folderRenamed = new EventEmitter<{ id: string; name: string }>();
  @Output() itemsMoved = new EventEmitter<MovedItems>();
  @Output() itemsListChange = new EventEmitter<ProcessedNode[]>();
  @ViewChild(TreeComponent, { static: true }) tree: TreeComponent;
  options: ITreeOptions = {
    idField: 'Id',
    childrenField: 'SubFolders',
    displayField: 'Name',
    allowDrag: node => node.data.Id !== VIRTUAL_FOLDER_ID,
    allowDrop: (node: TreeNode, { parent, index }) => {
      if (node.data.Type === CollectionItemType.Folder || node.data.Type === CollectionItemType.Project) {
        return parent.data.Level < this.maxNestingLevel;
      }

      return parent.data.Level <= this.maxNestingLevel;
    },
    actionMapping: {
      mouse: {
        drop: (tree: TreeModel, node, $event, { from, to }) => {
          if (from.data.Type === CollectionItemType.Folder) {
            const isAllowed = this.#foldersUtils.isMoveValid(this.#updatedFolders, from.data.Id, to.parent.data.Id);

            if (!isAllowed) {
              return;
            }

            const treeDepth = this.#foldersUtils.getMaxTreeDepth([from.data]);
            const dropFolderLevel = to.parent.data.Level;
            const isNestingLevelValid = dropFolderLevel + treeDepth <= this.maxNestingLevel;

            if (!isNestingLevelValid) {
              this.#matSnackBar.open('Folder hierarchy level exceeded', 'OK', { duration: 5000 });

              return;
            }
          }

          const { Id, Type } = from.data;
          const movedItems = this.#foldersUtils.getMovedItems(this.preselectedItemMetadata, Id, Type, to.parent.data.Id);

          this.#foldersService
            .move({ MovedItems: movedItems })
            .pipe(
              tap(() => this.itemsMoved.emit({ MovedItems: movedItems })),
              switchMap(() => this.#foldersService.get()),
              tap(folders => (this.#updatedFolders = this.#addNestingLevels(folders.RootFolders))),
              tap(() => {
                // For cases without server pagination
                if (Type === CollectionItemType.Folder) {
                  return;
                }
                const selectedNode: TreeNode = tree.getNodeById(this.selectedFolderId());

                if (!selectedNode) {
                  return;
                }

                let updatedItems = this.#getUpdatedListItems(selectedNode.data);
                if (this.selectedFolderId() !== VIRTUAL_FOLDER_ID) {
                  updatedItems = updatedItems.filter(item => item.Id !== Id);
                }
                this.itemsListChange.emit(updatedItems);
              }),
              takeUntil(this.#destroy$),
            )
            .subscribe();
        },
        click: (tree: TreeModel, node, $event) => {
          TREE_ACTIONS.TOGGLE_ACTIVE(tree, node, $event);
          const currentNodeId = node.data.Id;

          if (this.selectedFolderId() === currentNodeId) {
            return;
          }

          this.#selectedFolderId.set(currentNodeId);
        },
      },
    },
    allowDragoverStyling: true,
    animateExpand: true,
    scrollOnActivate: true,
    animateSpeed: 30,
    animateAcceleration: 1.2,
  };
  isLoading = signal(false);
  readonly #selectedFolderId = signal(VIRTUAL_FOLDER_ID);
  selectedFolderId = this.#selectedFolderId.asReadonly();
  selectedFolder: Signal<ProcessedNode | null> = computed(() => {
    const folderId = this.#selectedFolderId();

    return this.#foldersUtils.getElementById(this.nodes, folderId) ?? null;
  });
  #updatedFolders: ProcessedNode[] = [];
  #virtualRootFolder: VirtualRootFolderConfig = {
    name: 'All Data Sources',
    icon: {
      isRemoteMaterialIcon: true,
      iconName: 'settings_ethernet',
    },
  };
  readonly #destroy$ = new Subject<void>();

  readonly #iconRegistry = inject(MatIconRegistry);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #matDialog = inject(MatDialog);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #foldersService = inject(FoldersService);
  readonly #foldersUtils = inject(FoldersUtilsService);
  readonly #matSnackBar = inject(MatSnackBar);

  @Input() set virtualRootFolder(value: VirtualRootFolderConfig | undefined) {
    this.#virtualRootFolder = value;
    if (!value) {
      return;
    }
    this.#updatedFolders = this.#updatedFolders.map(folder => {
      if (folder.Id === VIRTUAL_FOLDER_ID) {
        return { ...folder, Name: this.virtualRootFolder.name };
      }

      return folder;
    });

    if (!this.virtualRootFolder.icon.isRemoteMaterialIcon) {
      this.#iconRegistry.addSvgIcon(
        this.virtualRootFolder.icon.iconName,
        this.#sanitizer.bypassSecurityTrustResourceUrl(this.virtualRootFolder.icon.localIconPath),
      );
    }
    this.#changeDetectorRef.markForCheck();
  }

  @Input() set folders(list: CollectionItem[]) {
    this.#updatedFolders = this.#addNestingLevels(list);
  }

  @Input() set selectFolder(folderId: string) {
    this.#selectedFolderId.set(folderId);
    const folder: TreeNode = this.tree.treeModel.getNodeById(folderId);
    folder.setActiveAndVisible();
  }

  get virtualRootFolder(): VirtualRootFolderConfig {
    return this.#virtualRootFolder;
  }

  get nodes(): ProcessedNode[] {
    return this.#updatedFolders;
  }

  constructor() {
    this.#iconRegistry
      .addSvgIcon('add-subfolder', this.#sanitizer.bypassSecurityTrustResourceUrl('/assets/img/icons/add_subfolder.svg'))
      .addSvgIcon('folder', this.#sanitizer.bypassSecurityTrustResourceUrl('/assets/img/icons/folder-outlined.svg'))
      .addSvgIcon('project', this.#sanitizer.bypassSecurityTrustResourceUrl('/assets/img/icons/project.svg'));
    let initialRun = true;
    effect(() => {
      const id = this.selectedFolderId();
      if (!initialRun) {
        this.selectedFolderChange.emit(id);
      }
      initialRun = false;
    });
  }

  ngAfterViewInit(): void {
    const root: TreeNode = this.tree.treeModel.getNodeById(VIRTUAL_FOLDER_ID);
    root.setActiveAndVisible();
    if (this.hideProjectFolders) {
      this.updateVisibility();
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  updateVisibility(): void {
    this.tree.treeModel.doForAll((node: TreeNode) => {
      if (node.data.Type === CollectionItemType.Project) {
        node.hide();
      }
    });
  }

  onTreeUpdate(): void {
    if (this.hideProjectFolders) {
      this.updateVisibility();
    }
  }

  isVirtualRootFolder(node: TreeNode): boolean {
    return node.data?.Id === VIRTUAL_FOLDER_ID;
  }

  getEntityIcon(node: TreeNode): string {
    if (node.data?.Type === CollectionItemType.Project) {
      return 'project';
    }

    return 'folder';
  }

  createFolder(contextMenuItemEvent?: ExecuteContextMenuItemEvent<ProcessedNode>): void {
    const { value } = contextMenuItemEvent || {};

    if (value?.Type === CollectionItemType.Project) {
      return;
    }

    if (value?.Level === this.maxNestingLevel) {
      return;
    }

    const innerFolderNames = value?.SubFolders.map(folder => folder.Name);

    this.isLoading.set(true);

    this.#matDialog
      .open<ManipulateFolderComponent, ManipulateFolderData, string>(ManipulateFolderComponent, {
        width: '352px',
        data: {
          headerTranslationLabel: 'folders.add',
          existingFolderNames: innerFolderNames,
        },
        autoFocus: 'input[matInput]',
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(name => {
          const createFolderData: CreateFolderData = { ParentFolderId: value?.Id || null, Name: name };

          return this.#foldersService.create(createFolderData).pipe(tap(folder => this.folderCreated.emit(folder.Id)));
        }),
        switchMap(() => this.#foldersService.get()),
        tap(folders => (this.#updatedFolders = this.#addNestingLevels(folders.RootFolders))),
        takeUntil(this.#destroy$),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe();
  }

  editFolder(contextMenuItemEvent: ExecuteContextMenuItemEvent<CollectionItem>): void {
    const { value } = contextMenuItemEvent;

    if (value.Type === CollectionItemType.Project) {
      return;
    }

    const innerFolderNames = value.SubFolders.map(folder => folder.Name);

    this.#matDialog
      .open<ManipulateFolderComponent, ManipulateFolderData, string>(ManipulateFolderComponent, {
        width: '352px',
        data: {
          folderName: value.Name,
          headerTranslationLabel: 'folders.edit',
          existingFolderNames: innerFolderNames,
        },
        autoFocus: 'input[matInput]',
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((Name: string) =>
          this.#foldersService.edit(value.Id, { Name }).pipe(
            tap(folder => {
              this.#updatedFolders = this.#foldersUtils.updateElement(this.#updatedFolders, folder.Id, { Name: folder.Name });
              const parentId = this.#foldersUtils.findParentId(this.#updatedFolders, folder.Id);
              const parentFolder = this.#foldersUtils.getElementById(this.#updatedFolders, parentId);
              const nodesForSort = parentFolder?.SubFolders ?? this.#updatedFolders.slice(1);
              const updatedChildren = this.#foldersUtils.sortFolders(nodesForSort);
              if (parentFolder) {
                const parentFolderClone = cloneDeep(parentFolder);
                parentFolderClone.SubFolders = updatedChildren;
                this.#updatedFolders = this.#foldersUtils.replaceNode(this.#updatedFolders, parentFolderClone);
              } else {
                this.#updatedFolders = [
                  {
                    Id: VIRTUAL_FOLDER_ID,
                    Name: this.virtualRootFolder.name,
                    Type: null,
                    SubFolders: [],
                    Level: 0,
                    IsEmpty: true,
                  },
                  ...updatedChildren,
                ];
              }
              this.folderRenamed.emit({ id: folder.Id, name: Name });
              this.#changeDetectorRef.markForCheck();
            }),
          ),
        ),
        takeUntil(this.#destroy$),
      )
      .subscribe();
  }

  deleteFolder(contextMenuItemEvent: ExecuteContextMenuItemEvent<CollectionItem>): void {
    const { value } = contextMenuItemEvent;

    if (value.Type === CollectionItemType.Project) {
      return;
    }

    if (value.SubFolders.length) {
      return;
    }

    this.#matDialog
      .open(DeleteFolderComponent, {
        width: '400px',
        autoFocus: false,
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.#foldersService.delete(value.Id)),
        tap(() => {
          if (value.Id === this.selectedFolderId()) {
            const node: TreeNode = this.tree.treeModel.getNodeById(value.Id);
            node.isRoot ? this.tree.treeModel.getNodeById(VIRTUAL_FOLDER_ID).toggleActivated() : node.parent.toggleActivated();
            const newId = node.isRoot ? VIRTUAL_FOLDER_ID : node.parent.data.Id;
            this.#selectedFolderId.set(newId);
          }
        }),
        switchMap(() => this.#foldersService.get()),
        tap(folders => {
          this.#updatedFolders = this.#addNestingLevels(folders.RootFolders);
          this.#changeDetectorRef.markForCheck();
        }),
        takeUntil(this.#destroy$),
      )
      .subscribe();
  }

  isAddDisabled = (item: ProcessedNode) => {
    return item.Type === CollectionItemType.Project || item.Level >= this.maxNestingLevel;
  };

  isEditDisabled = (item: ProcessedNode) => {
    return item.Type === CollectionItemType.Project;
  };

  isDeleteDisabled = (item: ProcessedNode) => {
    return item.Type === CollectionItemType.Project || !item.IsEmpty;
  };

  isExpanderVisible(children: TreeNode[]): boolean {
    if (!children?.length) {
      return false;
    }

    return children.some(item => !item.isHidden);
  }

  isVisible = (item: CollectionItem) => {
    return item.Id !== VIRTUAL_FOLDER_ID;
  };

  #addNestingLevels(list: CollectionItem[]): ProcessedNode[] {
    return this.#foldersUtils.addNestingLevels([
      {
        Id: VIRTUAL_FOLDER_ID,
        Name: this.virtualRootFolder.name,
        Type: null,
        SubFolders: [],
        IsEmpty: true,
      },
      ...list,
    ]);
  }

  #getUpdatedListItems(element: ProcessedNode): ProcessedNode[] {
    const items =
      this.selectedFolderId() === VIRTUAL_FOLDER_ID
        ? this.#foldersUtils.getAll(this.#updatedFolders)
        : this.#foldersUtils.getAllNodeChildren(element);

    // Case without server pagination - teams/projects page, items filtered by project type
    return items.filter(item => item.Type === CollectionItemType.Project);
  }
}
